import React, { useEffect, useMemo, useState } from "react";

import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import "./dateRange.css";
import CustomDateRangePicker from "./CustomDateRangePicker";
import SafeDefinedRange from "./SafeDefinedRange";
import ErrorBoundary from "../ErrorBoundary";
import { useDateFilter } from "../../hooks/useFilterContext";
import { useResponsive } from "../../hooks/customUseResponsive";
import { CustomButton } from "../Button";
import { buildCombinedStaticRanges, createDateRangeObject, findMatchingStaticRange } from "../../utils";

interface DateRangeProps {
  onClose: () => void;
  customStaticRanges: any[];
}

function DateRange({ onClose, customStaticRanges }: DateRangeProps) {
  const isMobile = useResponsive("down", "md");
  const { filters, setDateRange, resetDateRange } = useDateFilter();

  const [showCustomComponent, setShowCustomComponent] = useState(true);
  const [state, setState] = useState(() => {
    return createDateRangeObject(filters.startDate, filters.endDate);
  });
  const [focusedRange, setFocusedRange] = useState<[number, number]>([0, 0]);
  const [shownDate, setShownDate] = useState<Date>(() => {
    const initialRange = createDateRangeObject(filters.startDate, filters.endDate)[0];
    return initialRange?.startDate || new Date();
  });
  const [error, setError] = useState<string | null>(null);

  const staticRanges = useMemo(() => {
    return buildCombinedStaticRanges(customStaticRanges || []);
  }, [customStaticRanges]);

  const presetStaticRanges = useMemo(() => {
    return staticRanges.filter((range: { label: string }) => range.label !== "Custom");
  }, [staticRanges]);

  useEffect(() => {
    const nextRange = createDateRangeObject(filters.startDate, filters.endDate);
    setState(nextRange);
    setShownDate(nextRange[0]?.startDate || new Date());
  }, [filters.startDate, filters.endDate]);

  const setData = () => {
    const startDate = state[0]?.startDate;
    const endDate = state[0]?.endDate;

    if (!startDate || !endDate) {
      setError("Enter both dates before applying.");
      return;
    }

    if (startDate.getTime() > endDate.getTime()) {
      setError("Start date must be on or before end date.");
      return;
    }

    setDateRange(startDate, endDate);
    onClose();
  };

  const handleCustomDateChange = (item: any) => {
    try {
      setState([item.selection]);
    } catch (err) {
      console.error("Error in handleCustomDateChange:", err);
      setError("Date selection error occurred");
    }
  };

  const handleRangeFocusChange = (nextFocusedRange: [number, number]) => {
    setFocusedRange(nextFocusedRange);

    const selection = state[nextFocusedRange[0]];
    const nextShownDate = nextFocusedRange[1] === 1
      ? selection?.endDate
      : selection?.startDate;

    if (nextShownDate) {
      setShownDate(nextShownDate);
    }
  };

  const handleShowCustomComponent = () => {
    setShowCustomComponent(true);
  };

  const renderStaticRangeLabel = (range: any) => {
    if (range.label === "Custom") {
      return (
        <span
          className={`custom-static-range-label ${
            showCustomComponent
              ? "rdrStaticRangeSelected custom-active-color"
              : ""
          }`}
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleShowCustomComponent();
          }}
        >
          {range.label}
        </span>
      );
    }

    return range.label;
  };

  const handleChange = (item: any) => {
    try {
      const { startDate, endDate } = item.selection;

      const matchedStaticRange = findMatchingStaticRange(
        startDate,
        endDate,
        presetStaticRanges
      );

      setState([item.selection]);
      setDateRange(startDate, endDate);

      if (matchedStaticRange) {
        setShowCustomComponent(false);
        onClose();
        return;
      }

      setShowCustomComponent(true);
      setFocusedRange([0, 0]);
    } catch (err) {
      console.error("Error in handleChange:", err);
      setError("Date range selection error occurred");
    }
  };

  if (error) {
    return (
      <div className="d-grid">
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
          <button
            className="btn btn-sm btn-outline-danger ms-2"
            onClick={() => setError(null)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="d-grid">
        <div className="d-flex reverse-filter-section">
          <div>
            {((isMobile && !showCustomComponent) || !isMobile) && (
              <div>
                <SafeDefinedRange
                  onChange={handleChange}
                  moveRangeOnFirstSelection={false}
                  months={1}
                  ranges={state}
                  direction={isMobile ? "vertical" : "horizontal"}
                  inputRanges={[]}
                  staticRanges={staticRanges}
                  renderStaticRangeLabel={renderStaticRangeLabel}
                />
              </div>
            )}

            {showCustomComponent && (
              <div
                className={`date-filter-actions d-flex items-center ${isMobile ? "mt-2" : ""}`}
              >
                <CustomButton
                  variant="outlined"
                  onClick={() => {
                    setShowCustomComponent(false);
                    resetDateRange();
                    onClose();
                  }}
                  className="reset-date-picker reset-btn"
                >
                  Reset
                </CustomButton>
                <CustomButton
                  onClick={setData}
                  className="submit-date-picker"
                  variant="contained"
                >
                  Apply
                </CustomButton>
              </div>
            )}
          </div>

          {showCustomComponent && (
            <div>
             <CustomDateRangePicker
                state={state}
                handleCustomDateChange={handleCustomDateChange}
                showDateDisplay
                editableDateInputs
               moveRangeOnFirstSelection={false}
                retainEndDateOnFirstSelection
                focusedRange={focusedRange}
                onRangeFocusChange={handleRangeFocusChange}
                shownDate={shownDate}
                onShownDateChange={setShownDate}
                calendarKey={`${shownDate.getFullYear()}-${shownDate.getMonth()}-${focusedRange[1]}`}
                months={1}
                direction={isMobile ? "vertical" : "horizontal"}
                showSelectionPreview
                showPreview
                preventSnapRefocus
                maxDate={new Date()}
                dateDisplayFormat="P"
              />
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default DateRange;
